import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import { useTranslations } from '../../../hooks/useTranslations';
import { Option } from './components/Option';
import Chevron, { Direction } from '../icons/Chevron/Chevron';
import styles from './BlockSelect.module.css';
import {
  SelectOptionProps,
  SelectProps,
  IDefaultAriaAttrs,
  IAriaAttrs,
} from './types';
import { initialState, reducer } from './reducer';

const Select = ({
  controlRef,
  disabled,
  name,
  label,
  value,
  valueRender,
  options,
  error,
  system,
  hideLabel,
  searchable,
  clearable,
  outline,
  style,
  onChange,
  onFocus,
  onBlur,
  ariaDescribedBy,
}: SelectProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { active, activeOption, inputActive, query, showMenu } = state;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const hasError = error ? error.length > 0 : false;
  const isDisabled = !!disabled;

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { blockSelectDefaultLabel, blockSelectDefaultNoOptions } =
    useTranslations();

  const displayLabel = label ?? blockSelectDefaultLabel ?? 'Select';
  const displayNoOptionLabel = blockSelectDefaultNoOptions ?? 'No options';

  const {
    bodytextDefaultNone: bodyText,
    inputtextinputsNoneDisabled: inputStyleDisabled,
    inputtextinputsNoneNone: inputStyle,
  } = useTheme();

  const openListBox = useCallback(() => {
    if (system) return;
    if (disabled) return;
    if (showMenu && searchable) return;

    dispatch({
      type: 'OPEN_LISTBOX_AND_ACTIVATE_QUERY_INPUT',
      payload: { inputActive: searchable ?? false },
    });

    if (value) {
      const optionIndex = filteredOptions.findIndex(
        (option) => option.value === value.value,
      );

      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: { activeOption: optionIndex },
      });
    }

    inputRef.current?.focus();
  }, [disabled, filteredOptions, searchable, value, showMenu, system]);

  const selectAndCloseListBox = useCallback(() => {
    const value = filteredOptions[activeOption] ?? null;

    dispatch({
      type: 'SELECT_ITEM_AND_CLOSE_LISTBOX',
    });

    if (onChange) onChange(value);

    inputRef.current?.blur();
  }, [activeOption, filteredOptions, onChange]);

  // Todo: Make this custom hook for re-use
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!containerRef.current?.contains(target)) {
        dispatch({ type: 'OUTSIDE_CLICK' });
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [active, searchable]);

  useEffect(() => {
    const handleEnterKeyDown = () => {
      if (active) {
        if (showMenu) {
          selectAndCloseListBox();
        } else {
          openListBox();
        }
      }
    };

    const handleEscapeKeyDown = () => {
      dispatch({ type: 'CLOST_LISTBOX_AND_UNSET_QUERY_INPUT' });
      inputRef.current?.blur();
    };

    const handleArrowUp = () => {
      if (filteredOptions.length <= 0) return;
      const optionIndex =
        activeOption <= 0 ? filteredOptions.length - 1 : activeOption - 1;

      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: { activeOption: optionIndex },
      });
    };

    const handleArrowDown = () => {
      if (filteredOptions.length <= 0) return;
      if (!showMenu && active) {
        dispatch({
          type: 'SHOW_MENU_AND_ACTIVE_OPTION',
          payload: { inputActive: searchable ?? false },
        });

        if (value) {
          const optionIndex = filteredOptions.findIndex(
            (option) => option.value === value.value,
          );

          dispatch({
            type: 'SET_ACTIVE_OPTION',
            payload: { activeOption: optionIndex },
          });
        } else {
          dispatch({
            type: 'SET_ACTIVE_OPTION',
            payload: { activeOption: 0 },
          });
        }

        inputRef.current?.focus();

        return;
      }

      const optionIndex =
        activeOption === filteredOptions.length - 1 ? 0 : activeOption + 1;

      dispatch({
        type: 'SET_ACTIVE_OPTION',
        payload: { activeOption: optionIndex },
      });
    };

    const handleSpaceKeyDown = (event: KeyboardEvent) => {
      if (!active) return;

      // If the menu IS NOT open, but the list box is active, we open the list box.
      if (!showMenu) {
        event.preventDefault(); // prevents space from being captured when list box menu is in closed state.
        openListBox();
        return;
      }

      // If the menu IS open and NOT searchable, we select and close on spacebar (since the keyboard isn't supported for jump to).
      if (!searchable) {
        event.preventDefault(); // prevents space from being captured when list box menu doesn't accept entry.
        selectAndCloseListBox();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (system) return;

      switch (event.code) {
        case 'Enter':
          handleEnterKeyDown();
          break;
        case 'Escape':
          handleEscapeKeyDown();
          break;
        case 'ArrowUp':
          handleArrowUp();
          break;
        case 'ArrowDown':
          handleArrowDown();
          break;
        case 'Space':
          handleSpaceKeyDown(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    filteredOptions,
    activeOption,
    active,
    openListBox,
    system,
    searchable,
    value,
    showMenu,
    onChange,
    selectAndCloseListBox,
  ]);

  useEffect(() => {
    if (!showMenu) return;

    const option = containerRef.current?.querySelector(
      'li[aria-selected="true"]',
    );

    option?.scrollIntoView({ block: 'center' });
  }, [showMenu]);

  useEffect(() => {
    if (activeOption < 0) return;

    const option =
      containerRef.current?.getElementsByTagName('li')[activeOption];

    option?.scrollIntoView({ block: 'nearest' });
  }, [activeOption]);

  const handleFocus = () => {
    dispatch({ type: 'SET_ACTIVE', payload: { active: true } });
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    dispatch({ type: 'SET_ACTIVE', payload: { active: false } });
    if (onBlur) onBlur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.currentTarget.value;
    dispatch({ type: 'UPDATE_QUERY', payload: { query: eventValue } });
  };

  const handleOptionClick = (option: SelectOptionProps) => {
    dispatch({ type: 'OPTION_SELECTED' });
    if (onChange) onChange(option);
  };

  const handleDefaultChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const option = filteredOptions.find(
      (option) => option.value === event.currentTarget.value,
    );

    const value = option ?? null;

    if (onChange) onChange(value);
  };

  const handleArrowClick = () => {
    if (!showMenu) return;
    if (!searchable) return;

    dispatch({ type: 'CLOSE_SEARCHABLE_LISTBOX' });
  };

  const defaultAriaAttrs: IDefaultAriaAttrs = {
    'aria-disabled': isDisabled,
  };

  const activeOptionElement =
    activeOption >= 0
      ? containerRef.current?.getElementsByTagName('li')[activeOption]
      : undefined;
  const ariaAttrs: IAriaAttrs = system
    ? defaultAriaAttrs
    : {
        ...defaultAriaAttrs,
        'aria-label': displayLabel,
        'aria-expanded': showMenu,
        'aria-owns': `${name}-listbox`,
        'aria-haspopup': 'listbox',
        'aria-activedescendant': activeOptionElement
          ? activeOptionElement.id
          : undefined,
      };

  const controlClasses = clsx({
    [styles.selectControl]: true,
    [styles.selectControlActive]: active,
    [styles.selectControlOutline]: active && outline,
    [styles.selectControlAriaDisabled]: isDisabled,
    [styles.selectControlError]: hasError,
  });

  const selectedClasses = clsx({
    [styles.selected]: true,
    [styles.selectedNoLabel]: !label || hideLabel,
  });

  const selectArrowClasses = clsx({
    [styles.selectArrow]: true,
    [styles.selectArrowFocus]: active,
  });

  return (
    <div
      className={styles.selectContainer}
      ref={containerRef}
      style={{ ...bodyText, ...style?.container }}
    >
      {}
      <div
        ref={controlRef}
        className={controlClasses}
        role={system ? 'presentation' : 'combobox'}
        tabIndex={system ? -1 : 0}
        onClick={openListBox}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-describedby={system ? undefined : ariaDescribedBy}
        style={{
          ...(isDisabled ? inputStyleDisabled : inputStyle),
          ...style?.control,
        }}
        {...ariaAttrs}
      >
        {system && (
          <select
            className={styles.defaultSelect}
            defaultValue={value?.value}
            disabled={isDisabled}
            tabIndex={0}
            name={name}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleDefaultChange}
            aria-label={label}
            aria-describedby={ariaDescribedBy}
          >
            {clearable && <option />}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                selected={option.selected}
              >
                {option.label}
              </option>
            ))}
          </select>
        )}
        <div
          className={clsx({
            [styles.labelContainer]: true,
            [styles.labelContainerFocused]: inputActive || !!value,
          })}
        >
          <label
            className={styles.label}
            htmlFor={name}
            style={{ visibility: hideLabel ? 'hidden' : 'visible' }}
          >
            {displayLabel}
          </label>
        </div>
        <div
          className={clsx({
            [styles.inputContainer]: true,
            [styles.inputContainerNoLabel]: hideLabel,
          })}
        >
          {!inputActive && (
            <div
              className={selectedClasses}
              style={style?.selected}
              data-testid="selectedValueLabel"
            >
              {valueRender ?? value?.label}
            </div>
          )}
          {searchable && (
            <input
              className={clsx({
                [styles.input]: true,
                [styles.inputActive]: inputActive,
                [styles.disabledStyle]: isDisabled,
              })}
              disabled={isDisabled}
              ref={inputRef}
              id={name}
              name={name}
              type="text"
              value={query}
              autoComplete="off"
              tabIndex={-1}
              aria-autocomplete="list"
              aria-controls={showMenu ? `${name}-listbox` : undefined}
              aria-activedescendant={ariaAttrs['aria-activedescendant']}
              onChange={handleInputChange}
              style={isDisabled ? inputStyleDisabled : inputStyle}
            />
          )}
        </div>
        <div
          role="button"
          className={selectArrowClasses}
          onClick={handleArrowClick}
          tabIndex={-1}
        >
          <Chevron
            direction={Direction.Down}
            s={{
              color: 'currentColor',
              fill: 'currentColor',
              paddingBottom: '0.25rem',
              transform: `${showMenu ? `rotate(180deg)` : `none`}`,
              transformOrigin: 'center center',
              width: '0.875rem',
            }}
          />
        </div>
      </div>
      {showMenu && (
        <div
          className={styles.selectMenu}
          style={style?.selectMenu}
          role="presentation"
        >
          <ul className={styles.listBox} role="listbox" id={`${name}-listbox`}>
            {filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((option, index) => (
                  <Option
                    id={`${name}-${option.value}`}
                    key={option.value}
                    selected={value ?? null}
                    option={option}
                    active={activeOption === index}
                    onClick={handleOptionClick}
                  />
                ))}
              </>
            ) : (
              <div className={styles.noOptions}>
                <span>{displayNoOptionLabel}</span>
              </div>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export type { SelectOptionProps };

export default Select;
